import { Link } from "react-router-dom";
import { DOMAINS, DOMAIN_LABELS, type Action } from "@rrid/shared-types";
import { useAuth } from "../lib/auth.js";
import { useCan } from "../components/Can.js";
import { Badge, Card, CardBody } from "../components/ui.js";

// Access to actions inside each portal is enforced server-side (every write is re-checked) and
// reflected by each portal. Operations is the one portal hidden outright (July-8 FR-N4) — it must
// drop off BOTH this grid and the nav, or a student's "focused view" (US-N4) still advertises it.
const PORTALS: { to: string; title: string; desc: string; emoji: string; action?: Action }[] = [
  { to: "/preprints", title: "Find a Preprint", desc: "Browse, assign, pitch, and select preprints (Stage 1).", emoji: "📄" },
  { to: "/reviewers", title: "Find Reviewers", desc: "Rank, activate, propose reviewers, run COI checks (Stage 2).", emoji: "🔎" },
  { to: "/quality-check", title: "Quality Check", desc: "Review proposed reviewers, greenlight, double-check (Stage 3).", emoji: "✅" },
  { to: "/operations", title: "Operations", desc: "Janeway invites, review tracking, publishing (Stage 4).", emoji: "📣", action: "openOperations" },
];

export function Home(): JSX.Element {
  const { user } = useAuth();
  const can = useCan();
  const portals = PORTALS.filter((p) => !p.action || can(p.action));
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Welcome, {user?.name?.split(" ")[0] || "there"}.</h1>
        <p className="mt-1 text-slate-600">
          {portals.length === 4 ? "The four-stage editorial pipeline." : "Your part of the editorial pipeline."} Pick a
          portal to get started.
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {portals.map((p) => (
          <Link key={p.to} to={p.to}>
            <Card className="h-full transition hover:border-brand hover:shadow-md">
              <CardBody className="space-y-1">
                <div className="text-2xl">{p.emoji}</div>
                <h2 className="text-lg font-semibold text-brand">{p.title}</h2>
                <p className="text-sm text-slate-600">{p.desc}</p>
              </CardBody>
            </Card>
          </Link>
        ))}
      </div>
      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-500">Domains</h3>
        <div className="flex flex-wrap gap-2">
          {DOMAINS.map((d) => (
            <Badge key={d} tone="blue">
              {d} — {DOMAIN_LABELS[d]}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}
